import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentAPI, restaurantAPI } from '../../services/api';

function PaymentVerification() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentType = searchParams.get('type') || 'initial'; // 'initial', 'remaining', or 'plan-change'
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [message, setMessage] = useState('Verifying your payment...');
  const [attempts, setAttempts] = useState(0);
  const attemptsRef = useRef(0);
  const maxAttempts = 10;

  // Mounted guard — the verify loop retries via setTimeout. If the user
  // navigates away mid-loop, the pending setTimeout fires setState on an
  // unmounted component, which React warns about and also leaks state.
  // Every setState below is guarded by isMountedRef.
  const isMountedRef = useRef(true);
  // Track pending retry timeouts so we can cancel them on unmount.
  const retryTimeoutRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    verifyPayment();
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current !== null) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Safe setState wrappers — no-op after unmount.
  const safeSetStatus = (v) => { if (isMountedRef.current) setStatus(v); };
  const safeSetMessage = (v) => { if (isMountedRef.current) setMessage(v); };
  const safeSetAttempts = (v) => { if (isMountedRef.current) setAttempts(v); };
  const safeScheduleRetry = (fn, delay) => {
    if (!isMountedRef.current) return;
    retryTimeoutRef.current = setTimeout(() => {
      retryTimeoutRef.current = null;
      if (isMountedRef.current) fn();
    }, delay);
  };

  const verifyPayment = async () => {
    // Early bail-out if the component unmounted while we were scheduled.
    if (!isMountedRef.current) return;

    try {
      // Increment attempt counter
      attemptsRef.current += 1;
      safeSetAttempts(attemptsRef.current);

      console.log(`Payment verification attempt ${attemptsRef.current} of ${maxAttempts}`);

      // First, check if webhook already activated the subscription
      const restaurantResponse = await restaurantAPI.getSubscriptionPlanStatus();
      if (!isMountedRef.current) return;
      const restaurant = restaurantResponse.data.data;

      // Check payment status based on type
      if (paymentType === 'plan-change') {
        // Check if plan change payment is already completed
        if (restaurant.subscriptionPlan && restaurant.subscriptionPlan.type === 'one_time' &&
            restaurant.subscriptionPlan.oneTimeFee.initialPaymentStatus === 'paid' &&
            restaurant.subscriptionPlan.oneTimeFee.remainingPaymentStatus === 'paid') {
          safeSetStatus('success');
          safeSetMessage('Payment verified! Your subscription plan has been changed successfully.');
          safeScheduleRetry(() => navigate('/restaurant/dashboard'), 2000);
          return;
        }
      } else if (paymentType === 'remaining') {
        // Check if remaining payment is already completed
        if (restaurant.subscriptionPlan && restaurant.subscriptionPlan.type === 'one_time' &&
            restaurant.subscriptionPlan.oneTimeFee.remainingPaymentStatus === 'paid') {
          safeSetStatus('success');
          safeSetMessage('Payment verified! Your subscription is now fully paid.');
          safeScheduleRetry(() => navigate('/restaurant/profile?refresh=true'), 2000);
          return;
        }
      } else {
        // Check if initial payment is already completed
        if (restaurant.subscriptionPlan && restaurant.subscriptionPlan.type === 'one_time' &&
            restaurant.subscriptionPlan.oneTimeFee.initialPaymentStatus === 'paid') {
          safeSetStatus('success');
          safeSetMessage('Payment verified! Your restaurant is now active.');
          safeScheduleRetry(() => navigate('/restaurant/dashboard'), 2000);
          return;
        }
      }

      // If not activated yet and we have temp order ID, try manual verification
      if (restaurant.tempCashfreeOrderId) {
        const verifyResponse = paymentType === 'plan-change'
          ? await paymentAPI.verifyPlanChangePayment({ cashfreeOrderId: restaurant.tempCashfreeOrderId })
          : paymentType === 'remaining'
          ? await paymentAPI.verifyRemainingPayment({ cashfreeOrderId: restaurant.tempCashfreeOrderId })
          : await paymentAPI.verifySubscriptionPayment({ cashfreeOrderId: restaurant.tempCashfreeOrderId });

        if (!isMountedRef.current) return;

        if (verifyResponse.data.success) {
          safeSetStatus('success');
          const successMessage = paymentType === 'plan-change'
            ? 'Payment successful! Your subscription plan has been changed successfully.'
            : paymentType === 'remaining'
            ? 'Payment successful! Your subscription is now fully paid.'
            : 'Payment successful! Your restaurant is now active. Welcome to Foodwinc!';
          safeSetMessage(successMessage);
          safeScheduleRetry(() => {
            const redirectPath = paymentType === 'remaining'
              ? '/restaurant/profile?refresh=true'
              : '/restaurant/dashboard';
            navigate(redirectPath);
          }, 2000);
        } else {
          // Check if payment explicitly failed
          if (verifyResponse.data.data?.paymentStatus === 'FAILED') {
            safeSetStatus('failed');
            safeSetMessage('Payment failed. ' + (verifyResponse.data.data.message || 'Please try again or contact support.'));
            return;
          }

          // Payment not successful yet, retry if under max attempts
          if (attemptsRef.current < maxAttempts) {
            safeSetMessage(`Verifying payment... Attempt ${attemptsRef.current} of ${maxAttempts}`);
            safeScheduleRetry(() => verifyPayment(), 2000);
          } else {
            safeSetStatus('failed');
            safeSetMessage('Payment verification is taking longer than expected. Please contact support if the amount was deducted.');
          }
        }
      } else {
        // No order ID found, something went wrong
        safeSetStatus('failed');
        safeSetMessage('Payment session not found. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);

      // Retry if under max attempts
      if (attemptsRef.current < maxAttempts) {
        safeSetMessage(`Verifying payment... Attempt ${attemptsRef.current} of ${maxAttempts}`);
        safeScheduleRetry(() => verifyPayment(), 2000);
      } else {
        safeSetStatus('failed');
        safeSetMessage('Payment verification failed. Please contact support if the amount was deducted.');
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F8FAFC',
      padding: '24px'
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: '48px 32px',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.05)',
        animation: 'fadeIn 0.3s ease-in-out'
      }}>
        {/* Status Icon */}
        <div style={{
          width: '96px',
          height: '96px',
          backgroundColor: status === 'success'
            ? '#10B981'
            : status === 'failed'
            ? '#EF4444'
            : '#FF6961',
          borderRadius: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 32px',
          animation: status === 'verifying' ? 'pulse 1.5s ease-in-out infinite' : 'scaleIn 0.4s ease-out'
        }}>
          {status === 'verifying' && (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{
              animation: 'spin 2s linear infinite'
            }}>
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeLinecap="round"
                strokeDasharray="31.4 31.4" strokeDashoffset="10" opacity="0.3"/>
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeLinecap="round"
                strokeDasharray="31.4 31.4"/>
            </svg>
          )}
          {status === 'success' && (
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {status === 'failed' && (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          )}
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#0F172A',
          marginBottom: '16px',
          letterSpacing: '-0.5px'
        }}>
          {status === 'verifying' && 'Verifying Payment'}
          {status === 'success' && 'Payment Successful!'}
          {status === 'failed' && 'Verification Issue'}
        </h1>

        {/* Message */}
        <p style={{
          fontSize: '16px',
          color: '#64748B',
          marginBottom: '32px',
          lineHeight: '1.6'
        }}>
          {message}
        </p>

        {/* Progress Indicator for Verifying State */}
        {status === 'verifying' && (
          <div style={{
            marginBottom: '32px'
          }}>
            <div style={{
              width: '100%',
              height: '6px',
              backgroundColor: '#E2E8F0',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(attempts / maxAttempts) * 100}%`,
                height: '100%',
                backgroundColor: '#FF6961',
                borderRadius: '8px',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            <p style={{
              fontSize: '14px',
              color: '#94A3B8',
              marginTop: '12px'
            }}>
              Attempt {attempts} of {maxAttempts}
            </p>
          </div>
        )}

        {/* Transaction Details Card (for success) */}
        {status === 'success' && (
          <div style={{
            backgroundColor: '#F8FAFC',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            border: '1px solid #E2E8F0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 8V12L15 15" stroke="#10B981" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="12" r="9" stroke="#10B981" strokeWidth="2"/>
              </svg>
              <span style={{
                fontSize: '14px',
                color: '#64748B',
                fontWeight: '500'
              }}>
                Redirecting to dashboard...
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons for Failed State */}
        {status === 'failed' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}>
            <button
              onClick={() => window.location.href = 'mailto:support@foodwinc.com'}
              style={{
                backgroundColor: '#FF6961',
                color: '#FFFFFF',
                fontSize: '16px',
                fontWeight: '600',
                padding: '14px 24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(255, 105, 97, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#FF5449';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(255, 105, 97, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#FF6961';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(255, 105, 97, 0.2)';
              }}
            >
              Contact Support
            </button>
            <button
              onClick={() => navigate('/restaurant/plan-selection')}
              style={{
                backgroundColor: '#FFFFFF',
                color: '#64748B',
                fontSize: '16px',
                fontWeight: '600',
                padding: '14px 24px',
                borderRadius: '12px',
                border: '2px solid #E2E8F0',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#F8FAFC';
                e.target.style.borderColor = '#CBD5E1';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#FFFFFF';
                e.target.style.borderColor = '#E2E8F0';
              }}
            >
              Back to Plans
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.8);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }
      `}</style>
    </div>
  );
}

export default PaymentVerification;
